import React, { useState, useEffect } from 'react'
import UserPermissions from 'components/permissions/views/user'
import TagPermissions from 'components/permissions/views/tag'
import { IAccessPolicy } from 'types'
import { UserRoles, AccessResourceKinds, AccessAttributeKinds } from 'app-constants'
import api from 'helpers/api'
import { useApplicationStore } from 'hooks/application'
import { useProject } from 'hooks/useProject'
import Skeleton from 'components/skeleton'

interface Props {
  resource: AccessResourceKinds
  resourceId: string
  excludedPermissionCategories?: UserRoles[]
}

const Permissions: React.FC<Props> = ({ resource, resourceId, excludedPermissionCategories }) => {
  const { project } = useProject()
  const [permissionTab, setPermissionTab] = useState<string>('user')
  const [policies, setPolicies] = useState<Array<IAccessPolicy>>()
  const { displayErrorMessage, setSnackbarMessage } = useApplicationStore()

  const tabs = [
    { id: 'user', name: 'General Permissions', color: 'var(--color-green)', icon: 'type-person' },
    { id: 'tags', name: 'Permission Using Tags', color: 'var(--color-blue)', icon: 'type-tag' }
  ]

  const getResourcePolicies = async () => {
    try {
      const response = await api.getResourcePermissions(resource, resourceId)
      setPolicies(response.data)
    } catch (e) {
      displayErrorMessage(e)
      setPolicies(undefined)
    }
  }

  useEffect(() => {
    getResourcePolicies()
  }, [])

  const handleCreateAccessPolicy = async (
    role: UserRoles,
    attributeKind: AccessAttributeKinds,
    attributeId?: string,
    tagReferenceId?: string
  ) => {
    const current = policies || []
    try {
      const policy = {
        attributeKind,
        attributeId,
        role,
        resourceKind: resource,
        resourceId,
        tagReferenceId,
        context: { projectId: project.publicId }
      }

      // Special case if the resource is a table, the role should be owner
      if (resource === AccessResourceKinds.TABLE) {
        policy.role = UserRoles.OWNER
      }

      const response = await api.createAccessPolicy(policy)
      setPolicies([...current, response.data])
      setSnackbarMessage({ message: 'New permission added', status: 'success' })
    } catch (e) {
      displayErrorMessage(e)
    }
  }

  const handleUpdateAccessPolicy = async (policy: IAccessPolicy, role: UserRoles) => {
    // No_ACCESS is not a role type in the backend, instead we just delete the policy
    if (role == UserRoles.NO_ACCEESS) {
      try {
        await api.deleteAccessPolicy(project.publicId, policy)
      } catch (e) {
        displayErrorMessage(e)
        return
      }
      if (policies) {
        setPolicies(policies.filter((p) => p.publicId !== policy.publicId))
      }
      setSnackbarMessage({ message: 'Permission updated', status: 'success' })
    } else {
      let response: Awaited<ReturnType<typeof api.updateAccessPolicy>> | undefined
      try {
        response = await api.updateAccessPolicy(project.publicId, policy, role)
      } catch (e) {
        displayErrorMessage(e)
        return
      }
      if (policies) {
        setPolicies(policies.map((p) => (p.publicId === policy.publicId ? response!.data : p)))
      }
      setSnackbarMessage({ message: 'Permission updated', status: 'success' })
    }
  }

  const handleDeleteAccessPolicy = async (policy: IAccessPolicy) => {
    try {
      await api.deleteAccessPolicy(project.publicId, policy)
    } catch (e) {
      displayErrorMessage(e)
      return
    }
    if (policies) {
      setPolicies(policies.filter((p) => p.publicId !== policy.publicId))
    }
    setSnackbarMessage({ message: 'Permission deleted', status: 'success' })
  }

  const handleBulkAccessPolicy = async (
    role: UserRoles,
    attributeKind: AccessAttributeKinds,
    attributeIds?: string[],
    tagReferenceIds?: string[]
  ) => {
    const current = policies || []
    const policy = {
      attributeKind,
      attributeIds,
      role,
      resourceKind: resource,
      resourceId,
      tagReferenceIds,
      context: { projectId: project.publicId }
    }

    // Special case if the resource is a table, the role should be owner
    if (resource === AccessResourceKinds.TABLE) {
      policy.role = UserRoles.OWNER
    }
    const filteredPolicies = current.filter(
      (p) => !(p.role === policy.role && p.accessAttribute?.kind == attributeKind && p.accessResource?.kind == resource)
    )

    try {
      const response = await api.bulkPermissionsResource(policy)
      setPolicies([...filteredPolicies, ...response.data])
      setSnackbarMessage({ message: 'Permissions updated', status: 'success' })
    } catch (e) {
      displayErrorMessage(e)
    }
  }

  if (!policies) {
    return <Skeleton className="w-full mt-5 h-[300px]" />
  }

  return (
    <div className="flex flex-col h-full overflow-hidden select-none pt-5 pb-5 min-w-[300px]">
      <div className="static flex text-body justify-start overflow-x-auto overflow-y-hidden mb-2.5">
        <div
          className="flex justify-center items-center text-center cursor-pointer min-w-[200px] h-10 pb-2"
          style={{
            boxShadow: permissionTab === tabs[0].id ? `inset 0 -5px 0 0 ${tabs[0].color}` : 'inset 0 -5px 0 0 grey'
          }}
          onClick={() => setPermissionTab(tabs[0].id)}
        >
          <div className="font-medium text-inherit">{tabs[0].name}</div>
        </div>

        {project && (
          <div
            className="flex justify-center items-center text-center cursor-pointer min-w-[200px] h-10 pb-2"
            style={{
              boxShadow: permissionTab === tabs[1].id ? `inset 0 -5px 0 0 ${tabs[1].color}` : 'inset 0 -5px 0 0 grey'
            }}
            onClick={() => setPermissionTab(tabs[1].id)}
          >
            <div className="font-medium text-inherit">{tabs[1].name}</div>
          </div>
        )}
      </div>
      <UserPermissions
        policies={policies}
        display={permissionTab === 'user'}
        onCreatePolicy={handleCreateAccessPolicy}
        onUpdatePolicy={handleUpdateAccessPolicy}
        excludedPermissionCategories={excludedPermissionCategories}
      />
      <TagPermissions
        policies={policies}
        display={permissionTab === 'tags'}
        onCreatePolicy={handleCreateAccessPolicy}
        onDeletePolicy={handleDeleteAccessPolicy}
        onBulkSetPolicies={handleBulkAccessPolicy}
        excludedPermissionCategories={excludedPermissionCategories}
      />
    </div>
  )
}

export default Permissions
